import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Stack,
  Alert,
  Paper,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import VirtualizedPolygonList from './VirtualizedPolygonList';
import { endpoints, getAllTags, getAllCountries } from '../../services/apiClient';
import { useAccountContext } from '../../hooks/useAccountContext';
import { PolygonData } from '../../types/schema';
import YearPicker from './YearPicker';

interface DownloadDataModalProps {
  open: boolean;
  onClose: () => void;
  availableYears: number[];
  is_geometry: boolean;
}

const DownloadDataModal: React.FC<DownloadDataModalProps> = ({
  open,
  onClose,
  availableYears,
  is_geometry,
}) => {
  const { accountId } = useAccountContext();
  const [polygons, setPolygons] = useState<PolygonData[]>([]);
  const [selectedPolygonIds, setSelectedPolygonIds] = useState<Set<number>>(new Set());
  const [startYear, setStartYear] = useState<Date>(
    availableYears.length > 0
      ? new Date(availableYears[0], 0, 1)
      : new Date(new Date().getFullYear(), 0, 1)
  );
  const [endYear, setEndYear] = useState<Date>(
    availableYears.length > 0
      ? new Date(availableYears[availableYears.length - 1], 0, 1)
      : new Date(new Date().getFullYear(), 0, 1)
  );
  const [loading, setLoading] = useState(false);
  const [fetchingPolygons, setFetchingPolygons] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  // Fetch all polygons when modal opens or tags/country filter changes
  useEffect(() => {
    if (open) {
      fetchAllPolygons();
    } else {
      // Reset state when modal closes
      setSelectedPolygonIds(new Set());
      setSelectedTags([]);
      setSelectedCountries([]);
      setError(null);
    }
  }, [open, selectedTags, selectedCountries]);

  // Fetch available tags and countries when modal opens
  useEffect(() => {
    if (!open) return;
    getAllTags(accountId).then(setAvailableTags).catch(() => {});
    getAllCountries(accountId).then(setAvailableCountries).catch(() => {});
  }, [open]);

  // Update year defaults when availableYears changes
  useEffect(() => {
    if (availableYears.length > 0) {
      setStartYear(new Date(availableYears[0], 0, 1));
      setEndYear(new Date(availableYears[availableYears.length - 1], 0, 1));
    }
  }, [availableYears]);

  const fetchAllPolygons = async () => {
    setFetchingPolygons(true);
    setError(null);
    try {
      const minYear = availableYears.length > 0 ? availableYears[0] : new Date().getFullYear();
      const maxYear = availableYears.length > 0 ? availableYears[availableYears.length - 1] : new Date().getFullYear();

      const polygonsData = await endpoints.polygons(minYear, maxYear, accountId, is_geometry, selectedTags.length > 0 ? selectedTags : undefined, selectedCountries.length > 0 ? selectedCountries : undefined);
      setPolygons(polygonsData as PolygonData[] || []);
    } catch (err) {
      console.error('Failed to fetch polygons:', err);
      setError('Failed to load polygons. Please try again.');
    } finally {
      setFetchingPolygons(false);
    }
  };

  const handlePolygonToggle = (polygonId: number) => {
    setSelectedPolygonIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(polygonId)) {
        newSet.delete(polygonId);
      } else {
        newSet.add(polygonId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedPolygonIds(new Set(polygons.map(p => p.id)));
  };

  const handleDeselectAll = () => {
    setSelectedPolygonIds(new Set());
  };

  const handleDownload = async () => {
    // Validation
    if (selectedPolygonIds.size === 0) {
      setError('Please select at least one polygon.');
      return;
    }

    const startYearNum = startYear.getFullYear();
    const endYearNum = endYear.getFullYear();

    if (startYearNum > endYearNum) {
      setError('Start year must be less than or equal to end year.');
      return;
    }

    setDownloading(true);
    setError(null);

    try {
      const polygonIdsArray = Array.from(selectedPolygonIds);

      // Download both CSVs in parallel
      const [neeBlob, historyBlob] = await Promise.all([
        endpoints.dashboardDownloadNeeData(polygonIdsArray, startYearNum, endYearNum, accountId),
        endpoints.dashboardDownloadAttributeHistory(polygonIdsArray, startYearNum, endYearNum, accountId),
      ]);

      // Download both files
      const neeLink = document.createElement('a');
      neeLink.href = URL.createObjectURL(neeBlob.blob);
      neeLink.setAttribute('download', neeBlob.filename);
      document.body.appendChild(neeLink);
      neeLink.click();
      document.body.removeChild(neeLink);

      const historyLink = document.createElement('a');
      historyLink.href = URL.createObjectURL(historyBlob.blob);
      historyLink.setAttribute('download', historyBlob.filename);
      document.body.appendChild(historyLink);
      historyLink.click();
      document.body.removeChild(historyLink);

      // Close modal on success
      onClose();
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download data. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const selectedCount = selectedPolygonIds.size;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Download Data</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Filters Row: Year Range, Tags, Country */}
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Year Range
              </Typography>
              <Stack direction="row" spacing={2}>
                <YearPicker
                  label="Start Year"
                  value={startYear}
                  onChange={(date) => date && setStartYear(date)}
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                  availableYears={availableYears}
                />
                <YearPicker
                  label="End Year"
                  value={endYear}
                  onChange={(date) => date && setEndYear(date)}
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                  availableYears={availableYears}
                />
              </Stack>
            </Box>

            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Filter by Tags
              </Typography>
              <Autocomplete
                multiple
                options={availableTags}
                value={selectedTags}
                onChange={(_, newValue) => setSelectedTags(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select tags..." size="small" />
                )}
              />
            </Box>

            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Filter by Country
              </Typography>
              <Autocomplete
                multiple
                options={availableCountries}
                value={selectedCountries}
                onChange={(_, newValue) => setSelectedCountries(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select countries..." size="small" />
                )}
              />
            </Box>
          </Stack>

          {/* Polygon Selection */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                Select Polygons ({selectedCount} selected)
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={handleSelectAll}>
                  Select All
                </Button>
                <Button size="small" onClick={handleDeselectAll}>
                  Deselect All
                </Button>
              </Stack>
            </Stack>

              {fetchingPolygons ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress />
                </Box>
              ) : polygons.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No polygons found.</Typography>
                </Box>
              ) : (
                <VirtualizedPolygonList
                  polygons={polygons}
                  selectedPolygonIds={selectedPolygonIds}
                  onToggle={handlePolygonToggle}
                  height={500}
                />
              )}

          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={downloading}>
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          disabled={downloading || selectedPolygonIds.size === 0}
          startIcon={downloading ? <CircularProgress size={20} /> : null}
        >
          {downloading ? 'Downloading...' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadDataModal;
